import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, Select, Button, Spinner, Alert, Chip, EmptyState, activeDepartments } from '@glplus/glpweb-dsl';
import { adminApiClient, AdminApiError, type ApproveChangeBody, type ChangeField, type VendorChangeRequestItem } from '../../lib/adminApiClient';
import { DANGER_BTN } from '../../lib/buttonStyles';
import { Actions, Card, Field, MUTED, Panel, Photo, PRIMARY_BTN, SideBySide, day } from './parts';

/**
 * A vendor asking to change product information we lock (§15l), or the
 * description and photo of a new product, which a person reads before shoppers
 * do (§12, §13). We make the change or send it back with a reason the vendor
 * reads.
 */

export const FIELD_LABEL: Record<ChangeField, string> = {
  name: 'Name',
  department_category: 'Department',
  size: 'Size',
  description_short: 'Short description',
  description_long: 'Long description',
  ingredients: 'Ingredients',
  nutrition_label: 'Nutrition label photo',
  photo: 'Photo',
  name_es: 'Name (Spanish)',
  description_short_es: 'Short description (Spanish)',
  description_long_es: 'Long description (Spanish)',
  ingredients_es: 'Ingredients (Spanish)',
  other: 'Something else',
};

const TEXT_FIELDS: ChangeField[] = ['name', 'description_short', 'description_long', 'ingredients', 'name_es', 'description_short_es', 'description_long_es', 'ingredients_es'];
const LONG_FIELDS: ChangeField[] = ['description_long', 'ingredients', 'description_long_es', 'ingredients_es', 'description_short', 'description_short_es'];
const SIZE_UNITS = ['oz', 'lb', 'g', 'kg', 'fl oz', 'ml', 'l', 'pt', 'qt', 'gal', 'ct'];

/** What the product says today for the field, so the reviewer reads both. */
export function nowValue(r: VendorChangeRequestItem): string | null {
  const p = r.product;
  switch (r.field) {
    case 'name':
      return p.name;
    case 'department_category':
      return [p.department, p.category].filter(Boolean).join(' › ');
    case 'size':
      return p.size;
    case 'description_short':
      return p.descriptionShort;
    case 'description_long':
      return p.descriptionLong;
    case 'ingredients':
      return p.ingredients;
    case 'name_es':
      return p.nameEs;
    case 'description_short_es':
      return p.descriptionShortEs;
    case 'description_long_es':
      return p.descriptionLongEs;
    case 'ingredients_es':
      return p.ingredientsEs;
    default:
      return null;
  }
}

/** A size the vendor typed, read as a number and one of our units when it plainly is one. */
export function readSize(text: string | null): { value: string; unit: string } {
  const m = /^\s*(\d+(?:\.\d+)?)\s*([a-z ]+?)\s*$/i.exec(text ?? '');
  if (!m) return { value: '', unit: '' };
  const unit = m[2].toLowerCase().replace(/\s+/g, ' ');
  return SIZE_UNITS.includes(unit) ? { value: m[1], unit } : { value: m[1], unit: '' };
}

export function ChangeRequestsTab({ canReview, onCount }: { canReview: boolean; onCount: (n: number) => void }) {
  const [items, setItems] = useState<VendorChangeRequestItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminApiClient.listVendorChangeRequests('open');
      setItems(res.items);
      onCount(res.counts.open ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load change requests');
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!items)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <Spinner color="brand" />
      </Box>
    );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {flash && <Alert severity="success">{flash}</Alert>}
      {items.length === 0 ? (
        <EmptyState icon="listAlt" title="No change requests waiting" body="A vendor’s request to change product information shows here." />
      ) : (
        items.map((r) => (
          <RequestCard
            key={r.requestId}
            r={r}
            canReview={canReview}
            onDone={(msg) => {
              setFlash(msg);
              void load();
            }}
          />
        ))
      )}
    </Box>
  );
}

function RequestCard({ r, canReview, onDone }: { r: VendorChangeRequestItem; canReview: boolean; onDone: (msg: string) => void }) {
  const [open, setOpen] = useState<'approve' | 'reject' | null>(null);
  const isPhoto = r.field === 'photo' || r.field === 'nutrition_label';
  const label = FIELD_LABEL[r.field] ?? r.field;
  const productName = [r.product.brand, r.product.name].filter(Boolean).join(' — ');

  return (
    <Card testId={`vendor-request-${r.requestId}`}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>
          {label} — {productName}
        </Typography>
        <Chip label={r.vendor.businessName} size="small" />
        {r.ownProduct ? <Chip label="Their own product" size="small" /> : <Chip label="Other stores sell it too" size="small" color="warning" />}
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>
        Asked {day(r.createdAt)} · {r.product.glpProductId}
      </Typography>
      {!r.ownProduct && (
        <Typography sx={{ mt: 1, fontSize: 14 }}>A change here is what every store’s shoppers see for this product.</Typography>
      )}

      <SideBySide
        leftTitle="What it says now"
        rightTitle="What the vendor wants"
        left={
          isPhoto ? (
            <Photo src={r.field === 'photo' ? r.product.fullImageUrl : null} alt={`Our current ${label.toLowerCase()} of ${r.product.name}`} size={180} />
          ) : (
            <Typography sx={{ fontSize: 15, whiteSpace: 'pre-wrap' }} data-testid="vendor-request-now">
              {nowValue(r) || '—'}
            </Typography>
          )
        }
        right={
          <>
            {isPhoto ? (
              <Photo src={r.attachmentUrl} alt={`The vendor’s ${label.toLowerCase()} of ${r.product.name}`} size={180} />
            ) : (
              <Typography sx={{ fontSize: 15, whiteSpace: 'pre-wrap' }} data-testid="vendor-request-proposed">
                {r.proposedValue || '—'}
              </Typography>
            )}
            {r.reason && (
              <Box sx={{ mt: 1.5 }}>
                <Field label="Why">{r.reason}</Field>
              </Box>
            )}
          </>
        }
      />

      {canReview && (
        <Actions>
          <Button ariaLabel={`Make this change to ${r.product.name}`} buttonText="Make the change" variant="contained" size="small" sx={PRIMARY_BTN} dataTestId="vendor-request-approve" onClick={() => setOpen(open === 'approve' ? null : 'approve')} />
          <Button ariaLabel={`Send this request back to ${r.vendor.businessName}`} buttonText="Send back" variant="outlined" size="small" sx={DANGER_BTN} dataTestId="vendor-request-reject" onClick={() => setOpen(open === 'reject' ? null : 'reject')} />
        </Actions>
      )}
      {open === 'approve' && <ApprovePanel r={r} onDone={onDone} />}
      {open === 'reject' && <RejectPanel r={r} onDone={onDone} />}
    </Card>
  );
}

function useRun(onDone: (msg: string) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (work: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onDone(msg);
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function ApprovePanel({ r, onDone }: { r: VendorChangeRequestItem; onDone: (msg: string) => void }) {
  const isText = TEXT_FIELDS.includes(r.field);
  const guessed = readSize(r.proposedValue);
  const [value, setValue] = useState(r.proposedValue ?? '');
  const [sizeValue, setSizeValue] = useState(guessed.value);
  const [sizeUnit, setSizeUnit] = useState(guessed.unit);
  const [department, setDepartment] = useState('');
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const { busy, error, run } = useRun(onDone);

  const departments = useMemo(() => activeDepartments.map((d) => ({ value: d.label, label: d.label, categories: d.categories.map((c) => c.label) })), []);
  const categories = departments.find((d) => d.value === department)?.categories ?? [];

  const body = (): ApproveChangeBody | null => {
    const extra = note.trim() ? { note: note.trim() } : {};
    if (isText) return value.trim() ? { value: value.trim(), ...extra } : null;
    if (r.field === 'size') {
      const n = Number(sizeValue);
      return Number.isFinite(n) && n > 0 && sizeUnit ? { size: { sizeValue: n, sizeUnit }, ...extra } : null;
    }
    if (r.field === 'department_category') return department && category ? { department, category, ...extra } : null;
    return extra;
  };
  const ready = body() !== null;

  return (
    <Panel title="The change we make">
      {isText && (
        <TextField label="Exactly as shoppers will read it" ariaLabel="The wording we use" multiline={LONG_FIELDS.includes(r.field)} minRows={LONG_FIELDS.includes(r.field) ? 3 : undefined} value={value} onChange={(e) => setValue(e.target.value)} dataTestId="vendor-request-value" />
      )}
      {r.field === 'size' && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Box sx={{ width: 140 }}>
            <TextField label="Amount" ariaLabel="Size amount" type="number" value={sizeValue} onChange={(e) => setSizeValue(e.target.value)} dataTestId="vendor-request-size-value" />
          </Box>
          <Select ariaLabel="Size unit" label="Unit" options={[{ value: '', label: 'Unit…' }, ...SIZE_UNITS.map((u) => ({ value: u, label: u }))]} value={sizeUnit} onChange={(_e, v) => setSizeUnit(String(v))} size="small" native sx={{ width: 140 }} />
        </Box>
      )}
      {r.field === 'department_category' && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Select
            ariaLabel="Department"
            label="Department"
            options={[{ value: '', label: 'Department…' }, ...departments.map((d) => ({ value: d.value, label: d.label }))]}
            value={department}
            onChange={(_e, v) => {
              setDepartment(String(v));
              setCategory('');
            }}
            size="small"
            native
            sx={{ width: 240 }}
          />
          <Select ariaLabel="Category" label="Category" options={[{ value: '', label: 'Category…' }, ...categories.map((c) => ({ value: c, label: c }))]} value={category} onChange={(_e, v) => setCategory(String(v))} size="small" native disabled={!department} sx={{ width: 260 }} />
        </Box>
      )}
      {r.field === 'photo' && <Typography sx={{ fontSize: 14 }}>We make our own copy of the vendor’s photo and show that. If the file cannot be used, nothing changes and the request stays here.</Typography>}
      {r.field === 'nutrition_label' && <Typography sx={{ fontSize: 14 }}>Approving keeps the label photo on record. Enter the nutrition facts in the product editor if they need changing.</Typography>}
      {r.field === 'other' && <Typography sx={{ fontSize: 14 }}>Make the change in the product editor first; approving tells the vendor it is done.</Typography>}
      <Box sx={{ mt: 1.5 }}>
        <TextField label={`A word to ${r.vendor.businessName} (optional)`} ariaLabel="Note to the vendor" value={note} onChange={(e) => setNote(e.target.value)} dataTestId="vendor-request-note" />
      </Box>
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button
          ariaLabel="Make the change"
          buttonText={busy ? 'Saving…' : 'Make the change'}
          variant="contained"
          size="small"
          sx={PRIMARY_BTN}
          disabled={busy || !ready}
          dataTestId="vendor-request-approve-go"
          onClick={() => {
            const b = body();
            if (b) void run(() => adminApiClient.approveVendorChangeRequest(r.requestId, b), `Changed. ${r.vendor.businessName} sees it was made.`);
          }}
        />
      </Box>
    </Panel>
  );
}

function RejectPanel({ r, onDone }: { r: VendorChangeRequestItem; onDone: (msg: string) => void }) {
  const [note, setNote] = useState('');
  const { busy, error, run } = useRun(onDone);
  return (
    <Panel title="Why are we not making it?">
      <TextField label={`${r.vendor.businessName} reads this`} ariaLabel="Why we are not making the change" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} dataTestId="vendor-request-reason" />
      {error && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button ariaLabel="Send it back" buttonText={busy ? 'Sending…' : 'Send back'} variant="contained" size="small" sx={PRIMARY_BTN} disabled={busy || !note.trim()} dataTestId="vendor-request-reject-go" onClick={() => void run(() => adminApiClient.rejectVendorChangeRequest(r.requestId, note.trim()), `Sent back to ${r.vendor.businessName} with your reason.`)} />
      </Box>
    </Panel>
  );
}
